// src/pages/Profile/ProfileNii.tsx

import { FC } from 'react';
import styles from '../../css/ProfilePage.module.css';
import { motion, Variants } from 'framer-motion';
import { ArrowLeft, Linkedin, Mail, Scale, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

// Ensure you have the correct image file for Nii
import NiiImg from '../../assets/images/ProfileImage4.jpeg'; 

const ProfileNii: FC = () => {
    
    const fadeInUp: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.6, ease: "easeOut" } 
        }
    };

    const staggerContainer: Variants = {
        visible: {
            transition: { staggerChildren: 0.1 }
        }
    };

    return (
        <div className={styles.profilePage}>
            
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    
                    {/* Back Navigation */}
                    <div className={styles.backLinkWrapper}>
                        <Link to="/about" className={styles.backLink}>
                            <ArrowLeft size={16} /> Back to Team
                        </Link>
                    </div>

                    <div className={styles.profileHeader}>
                        
                        {/* Left: Image & Socials */}
                        <motion.div 
                            className={styles.imageColumn}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className={styles.profileImageWrapper}>
                                <img src={NiiImg} alt="Nii Armah Addy Esq" className={styles.profileImage} />
                            </div>
                            <div className={styles.socialRow}>
                                <a 
                                    href="https://www.linkedin.com/in/nii-armah-addy-19968b152/?originalSubdomain=gh" 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className={styles.socialBtn}
                                    aria-label="LinkedIn Profile"
                                >
                                    <Linkedin size={20} />
                                </a>
                                <a 
                                    href="mailto:contact@otatech.com" 
                                    className={styles.socialBtn}
                                    aria-label="Contact"
                                >
                                    <Mail size={20} />
                                </a>
                            </div>
                        </motion.div>

                        {/* Right: Biography */}
                        <motion.div 
                            className={styles.infoColumn}
                            initial="hidden"
                            animate="visible"
                            variants={staggerContainer}
                        >
                            <motion.h1 className={styles.name} variants={fadeInUp}>
                                Nii Armah Addy Esq
                            </motion.h1>
                            <motion.div className={styles.title} variants={fadeInUp}>
                                Non-Executive Director (Legal & Governance)
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Role at OTA TECH</h2>
                                <p className={styles.bioText}>
                                    Nii Armah serves as a <strong>Non-Executive Director</strong>, bringing critical legal oversight and corporate governance expertise to the board. 
                                </p>
                                <p className={styles.bioText}>
                                    As OTA TECH builds its core ERP infrastructure, he advises on the legal frameworks, regulatory compliance, and strategic risk management required for enterprise-grade software.
                                </p>
                                <p className={styles.bioText}>
                                    His role ensures that the company's growth and technological advancements are grounded in sound legal practice and ethical leadership standards.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Professional Background</h2>
                                <p className={styles.bioText}>
                                    Nii Armah is a <strong>Lawyer</strong>, <strong>Chartered Management Consultant (CMC)</strong>, and <strong>Chartered Professional Administrator (ChPA)</strong> with over 20 years of industrial experience in both the public and private sectors.
                                </p>
                                <p className={styles.bioText}>
                                    He is the Founder of the Institute of Leadership and Management in Education (InLaME) and the Institute of Professional Event Management (IPEM). His legal experience includes a stint with the <strong>International Criminal Court (ICC)</strong> in Rwanda.
                                </p>
                                <p className={styles.bioText}>
                                    Previously, he served as a lecturer and Governing Council member at Academic City College (formerly Sikkim Manipal University, Ghana) for nearly a decade.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Expertise & Impact</h2>
                                <div className={styles.skillsGrid} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                                    <div>
                                        <strong><Scale size={14} style={{display:'inline', marginRight:'6px'}}/> Legal & Governance</strong>
                                        <p style={{fontSize:'0.9rem', color:'var(--zinc-500)'}}>LLB, PGDLP, Corporate Law, Regulatory Compliance, Policy Formulation.</p>
                                    </div>
                                    <div>
                                        <strong><BookOpen size={14} style={{display:'inline', marginRight:'6px'}}/> Strategy & Education</strong>
                                        <p style={{fontSize:'0.9rem', color:'var(--zinc-500)'}}>Chartered Management Consultant, Published Author, Corporate Training.</p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Publications & Thought Leadership</h2>
                                <p className={styles.bioText}>
                                    A prolific writer, Nii Armah has authored five books, including <em>"The crucifixion of LI 1296"</em> and <em>"Leadership Framework for Project Management"</em>.
                                </p>
                                <p className={styles.bioText}>
                                    He is a weekly columnist for the <strong>Business & Financial Times</strong>, where he writes on Education & Management, with over 200 articles cited in universities globally.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Education</h2>
                                <p className={styles.bioText}>
                                    He holds a Bachelor of Arts (Hons), two Master's Degrees (UK and Ghana), an LLB, and a Postgraduate Diploma in Legal Practice (PGDLP).
                                </p>
                            </motion.div>

                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>
        </div>
    );
};

export default ProfileNii;